import { useFrontCodeStore } from '~/stores/display/front-code-store'
import type { GADataGtmBody, GAItem } from '~/types/common/shop-ga'
import type { PlanShopDetail, PlanShopListDto } from '~/types/display/plan'
import type { Corner, Template } from '~/types/display/shop'
import type { ProductItem } from '~/types/productItem'

const { generateGTMData, generatePageTitle } = useGAHandler()

export function generateShopSection(template: Template, corner: Corner, conrIdx: number) {
  return generateGTMData({ label1: template?.tmplNm, label2: corner?.conrNm, index: conrIdx + 1 })
}

export function generateBrandShopPage() {
  return generateGTMData({ page_title: generatePageTitle(['띵샵', '브랜드관']), screen_id: 'SB-MO-DP-00114' })
}

export function generateCategoryShopPage(categoryNm: string = '', depth: string = '') {
  const isLargeCategory = depth === '1'
  const largeCategoryScreenId = 'SB-MO-DP-00113'
  const middleCategoryScreenId = 'SB-MO-DP-00122'
  const screenId = isLargeCategory ? largeCategoryScreenId : middleCategoryScreenId

  return generateGTMData({ page_title: generatePageTitle(['띵샵', '카테고리', categoryNm]), screen_id: screenId })
}

export function generatePlanShopListPage() {
  return generateGTMData({ page_title: generatePageTitle(['띵샵', '기획전']), screen_id: 'SB-MO-DP-00110' })
}

export function generatePlanShopListGTMBody(planShopInfo: PlanShopListDto, index: number) {
  const gtmData: GADataGtmBody = {}
  gtmData.label1 = '기획전 목록'
  gtmData.label2 = planShopInfo.title
  gtmData.index = padNumber(index + 1)

  gtmData.cts_id = planShopInfo.mkdpNo
  gtmData.cts_name = planShopInfo.title

  return generateGTMData(gtmData)
}

export function generatePlanShopDetailPage(planShopDetail: PlanShopDetail) {
  const planShopNo = planShopDetail.mkdpNo

  return generateGTMData({
    page_title: generatePageTitle(['띵샵', '기획전', '기획전 상세', planShopNo]),
    screen_id: 'SB-MO-DP-00111',
  })
}

export function generatePlanShopDetailDividerGTMBody(divider: any, index: number) {
  const gtmData: GADataGtmBody = {}
  gtmData.label1 = divider.id
  gtmData.label2 = divider.title
  gtmData.index = padNumber(index + 1)

  gtmData.cts_id = divider.id
  gtmData.cts_name = divider.title

  return generateGTMData(gtmData)
}

export function generatePlanShopDetailProductTabGTMBody(divider: any, index: number) {
  const gtmData: GADataGtmBody = {}
  gtmData.label1 = divider.id
  gtmData.label2 = divider.title
  gtmData.index = padNumber(index + 1)

  gtmData.cts_id = divider.id
  gtmData.cts_name = divider.title

  return generateGTMData(gtmData)
}

export function generateProductItemVisibility(item: ProductItem, index: number = 0) {
  return JSON.stringify({
    ecommerce: {
      items: [createEcommerceItem(item, index)],
    },
  })

  function createEcommerceItem(item: ProductItem, index: number) {
    const ecommerceItem: GAItem = {}
    ecommerceItem.item_list_id = item.gaProperty?.itemListId // {{페이지정보}}_{{코너ID}}
    ecommerceItem.item_id = item.id // {{상품ID}},
    ecommerceItem.item_name = item.title // {{상품명}}
    ecommerceItem.index = padNumber(index) // {{000부터 순차}}
    ecommerceItem.item_brand = item.gaProperty?.brandNo // {{상품브랜드}}
    ecommerceItem.item_category = item.gaProperty?.stdLrgCtgNo // {{상품표준카테고리대분류}}
    ecommerceItem.item_category2 = item.gaProperty?.stdMidCtgNo // {{상품표준카테고리중분류}}
    ecommerceItem.item_category3 = item.gaProperty?.stdSmlCtgNo // {{상품표준카테고리소분류}}
    ecommerceItem.item_category4 = item.gaProperty?.stdThnCtgNo // {{상품표준카테고리세분류}}
    ecommerceItem.item_list_name = item.gaProperty?.tabName

    ecommerceItem.price = Number(item.price?.replace(/,/g, '')) // {{상품최종판매가}}
    ecommerceItem.coupon_yn = item.gaProperty?.couponAppliedYn // {{상품쿠폰-Y/N}}
    ecommerceItem.discount = item.gaProperty?.couponAppliedAmount // {{상품할인액}}

    return ecommerceItem
  }
}

export function getScreenIdByShopNo(shopNo: string) {
  try {
    const frontCodeStore = useFrontCodeStore()
    const frontCode = frontCodeStore.getFrontCode
    if (!frontCode) {
      return ''
    }
    const keys = Object.keys(frontCode)
    for (const key of keys) {
      if (frontCode[key]?.ref1Val === shopNo) {
        return frontCode[key].ref3Val
      }
    }
  }
  catch (e) {
    console.log('getScreenIdByShopNo error: ', e)
  }

  return ''
}

export function useCornerGa() {
  const makeSectionBody = (label1: string, label2: string, index: number = 0) => {
    return generateGTMData({ label1, label2, index: padNumber(index) })
  }

  const makeVisibilityBody = (cts_id: string | number, cts_name: string, index: number) => {
    return generateGTMData({ cts_id, cts_name: replaceNewlinesWithSpace(cts_name), index: padNumber(index) })
  }

  const makeClickBody = (label: string = '', cts_id: string | number, cts_name: string = '', index: number) => {
    return generateGTMData({
      label: replaceNewlinesWithSpace(label),
      cts_id,
      cts_name: replaceNewlinesWithSpace(cts_name),
      index: padNumber(index),
    })
  }

  return {
    makeSectionBody,
    makeVisibilityBody,
    makeClickBody,
  }
}

export function replaceNewlinesWithSpace(str: string) {
  if (!str) {
    return str
  }
  return str.replace(/\n/g, ' ')
}

export function padNumber(number: number) {
  return number.toString().padStart(3, '0')
}
